<!-- src/pages/projects/_form.vue -->
<template>
  <div class="row mb-3">
    <!-- Customer -->
    <div class="col-md-6 position-relative">
      <label for="customer" class="form-label">{{ $t('page.projects.inputs.customer') }}</label>
      <div class="input-group">
        <input
          id="customer"
          class="form-control"
          :class="{ 'is-invalid': errors.customer_id }"
          v-model="customerSearch"
          @input="searchCustomers"
          @focus="showCustomerDropdown = true"
          @blur="hideDropdown"
          :placeholder="$t('page.projects.inputs.customerPlaceholder')"
          autocomplete="off"
          type="text"
        />
        <button class="btn btn-outline-primary" type="button" @click="openCreateCustomerModal">
          <i class="fa fa-plus"></i>
        </button>
      </div>
      <input id="customer_id" v-model="formLocal.customer_id" type="hidden" required />
      <div v-if="errors.customer_id" class="invalid-feedback">
        {{ errors.customer_id[0] }}
      </div>

      <!-- Autocomplete Dropdown -->
      <ul
        v-if="showCustomerDropdown && filteredCustomers.length"
        class="list-group position-absolute w-100 zindex-dropdown"
      >
        <li
          v-for="c in filteredCustomers"
          :key="c.id"
          class="list-group-item list-group-item-action"
          @mousedown.prevent="selectCustomer(c)"
        >
          <div class="fw-bold">{{ c.full_name || c.name }}</div>
          <small class="text-muted" v-if="c.email">{{ c.email }}</small>
        </li>
      </ul>
    </div>

    <!-- Services -->
    <div class="col-md-6">
      <label for="service_id" class="form-label">{{ $t('page.projects.inputs.service_id') }}</label>
      <select
        id="service_id"
        class="form-select"
        :class="{ 'is-invalid': errors.service_id }"
        v-model="formLocal.service_id"
      >
        <option v-for="s in services" :key="s.id" :value="s.id">{{ s.title }}</option>
      </select>
      <div v-if="errors.service_id" class="invalid-feedback">
        {{ errors.service_id[0] }}
      </div>
    </div>

    <!-- Project Name -->
    <div class="col-md-8">
      <label for="name" class="form-label">{{ $t('page.projects.inputs.name') }}</label>
      <input
        id="name"
        class="form-control"
        :class="{ 'is-invalid': errors.name }"
        v-model="formLocal.name"
        :placeholder="$t('page.projects.inputs.namePlaceholder')"
        minlength="3"
        maxlength="50"
        type="text"
        required
      />
      <div v-if="errors.name" class="invalid-feedback">
        {{ errors.name[0] }}
      </div>
    </div>

    <!-- Version -->
    <div class="col-md-4">
      <label for="version" class="form-label">{{ $t('page.projects.inputs.version') }}</label>
      <input
        id="version"
        class="form-control"
        :class="{ 'is-invalid': errors.version }"
        v-model="formLocal.version"
        :placeholder="$t('page.projects.inputs.versionPlaceholder')"
        step="1"
        min="1"
        max="1000"
        type="number"
        required
      />
      <div v-if="errors.version" class="invalid-feedback">
        {{ errors.version[0] }}
      </div>
    </div>

    <!-- Modal Create Customer -->
    <div
      v-if="showCreateCustomer"
      class="modal fade show d-block"
      tabindex="-1"
      style="background: rgba(0,0,0,0.5);"
    >
      <div class="modal-dialog modal-md">
        <div class="modal-content">
          <div class="modal-header">
            <h6 class="modal-title">Create Customer</h6>
            <button type="button" class="btn-close" @click="showCreateCustomer = false"></button>
          </div>
          <div class="modal-body">
            <input
              v-model="newCustomerName"
              type="text"
              class="form-control"
              placeholder="Customer name"
            />
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary btn-sm" @click="showCreateCustomer = false">
              Cancel
            </button>
            <button class="btn btn-primary btn-sm" @click="createCustomer">Create</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted } from "vue";
import { useCustomerStore } from "@/stores/customerStore";

const props = defineProps({
  form: Object,
  errors: Object,
});

const emit = defineEmits(["update:form"]);

const formLocal = ref({ ...props.form });
const customerStore = useCustomerStore();

const customerSearch = ref("");
const filteredCustomers = ref([]);
const services = ref([]);

const showCustomerDropdown = ref(false);
const showCreateCustomer = ref(false);
const newCustomerName = ref("");

// Buscar clientes conforme digita
const searchCustomers = async () => {
  if (!customerSearch.value) {
    filteredCustomers.value = [];
    return;
  }
  await customerStore.getCustomerSearch(customerSearch.value);
  filteredCustomers.value = customerStore.customers;
};

// Selecionar cliente
const selectCustomer = async (customer) => {
  formLocal.value.customer_id = customer.id;
  customerSearch.value = customer.full_name || customer.name;
  showCustomerDropdown.value = false;
  await loadServices(customer.id);
};

// Carregar serviços do cliente selecionado
const loadServices = async (customerId) => {
  await customerStore.getCustomerService(customerId);
  services.value = customerStore.services;
};

// Esconde o dropdown (com delay para evitar clique perdido)
const hideDropdown = () => setTimeout(() => (showCustomerDropdown.value = false), 200);

// Criar novo cliente
const openCreateCustomerModal = () => {
  newCustomerName.value = "";
  showCreateCustomer.value = true;
};

const createCustomer = async () => {
  if (!newCustomerName.value.trim()) return;
  const response = await customerStore.createCustomer({ name: newCustomerName.value });
  if (response && response.data) {
    const newCustomer = response.data;
    formLocal.value.customer_id = newCustomer.id;
    customerSearch.value = newCustomer.full_name || newCustomer.name;
    await loadServices(newCustomer.id);
  }
  showCreateCustomer.value = false;
};

// Ao montar, preencher dados no modo de edição
onMounted(async () => {
  if (formLocal.value.customer_id) {
    const customer = await customerStore.getCustomer(formLocal.value.customer_id);
    customerSearch.value = customer.full_name || customer.name;
    await loadServices(formLocal.value.customer_id);
  }
});

// Atualizar form pai
watch(
  formLocal,
  (newVal) => emit("update:form", newVal),
  { deep: true }
);
</script>

<style scoped>
.zindex-dropdown {
  z-index: 1055;
  max-height: 200px;
  overflow-y: auto;
}
</style>
